export const YT_EMOJI_KIND = 'YT_EMOJI'
export const WALL_EMOJI_PER_MESSAGE = 10
const MAX_EMOJI_LENGTH = 32
export const SPREAD_WINDOW_MS = 10000
export const MIN_GAP_MS = 120
export const MAX_GAP_MS = 1500
export const MAX_QUEUED_MESSAGES = 300
const SEEN_CHUNKS_MAX = 400

export const WALL_EMOJI_FONT_FAMILY = "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Segoe UI Symbol', sans-serif"

export const youtubeListOf = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string' && x !== '') : []

export const isWallEmojiString = (raw: unknown): raw is string => {
  if (typeof raw !== 'string' || raw === '' || raw.length > MAX_EMOJI_LENGTH) return false
  for (const ch of raw) {
    const cp = ch.codePointAt(0) ?? 0
    if (cp < 0x80 && !(cp === 0x23 || cp === 0x2a || (cp >= 0x30 && cp <= 0x39))) return false
  }
  return true
}

export interface ChunkMemory {
  has: (chunkId: string) => boolean
  add: (chunkId: string) => void
}

export const createChunkMemory = (max = SEEN_CHUNKS_MAX): ChunkMemory => {
  const seen = new Set<string>()
  return {
    has: (chunkId) => seen.has(chunkId),
    add: (chunkId) => {
      seen.add(chunkId)
      if (seen.size > max) {
        const keep = Array.from(seen).slice(-Math.floor(max / 2))
        seen.clear()
        keep.forEach((k) => seen.add(k))
      }
    }
  }
}

export interface WallAddress {
  tag: string | null
  listEmpty: boolean
}

export const WALL_TAG_LENGTH = 16

export const wallTagOf = async (emoteWallId: string): Promise<string | null> => {
  try {
    const subtle = globalThis.crypto?.subtle
    if (subtle == null) return null
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(emoteWallId))
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('').slice(0, WALL_TAG_LENGTH)
  } catch {
    return null
  }
}

const addressedToThisWall = (e: Record<string, unknown>, address: WallAddress): boolean => {
  if (!('walls' in e)) return address.listEmpty
  const walls = e.walls
  if (!Array.isArray(walls)) return false
  return address.tag != null && walls.includes(address.tag)
}

export const wallEmojiGroupsOf = (
  envelope: unknown,
  acceptedChannels: readonly string[],
  chunks: ChunkMemory,
  address: WallAddress
): string[][] | null => {
  if (envelope == null || typeof envelope !== 'object') return null
  const e = envelope as Record<string, unknown>
  if (e.kind !== YT_EMOJI_KIND) return null
  if (typeof e.channelId !== 'string' || !acceptedChannels.includes(e.channelId)) return null
  if (!addressedToThisWall(e, address)) return null
  if (typeof e.chunkId !== 'string' || e.chunkId === '' || chunks.has(e.chunkId)) return null
  if (!Array.isArray(e.emoji)) return null
  chunks.add(e.chunkId)
  return e.emoji
    .map((group: unknown) => (Array.isArray(group) ? group.filter(isWallEmojiString).slice(0, WALL_EMOJI_PER_MESSAGE) : []))
    .filter((group) => group.length > 0)
}

export const spreadGapMs = (queued: number): number => {
  if (!(queued > 0)) return MAX_GAP_MS
  return Math.min(MAX_GAP_MS, Math.max(MIN_GAP_MS, Math.round(SPREAD_WINDOW_MS / queued)))
}

export const enqueueWallEmoji = (queue: readonly string[][], groups: readonly string[][]): string[][] => {
  const next = [...queue, ...groups]
  return next.length > MAX_QUEUED_MESSAGES ? next.slice(-MAX_QUEUED_MESSAGES) : next
}
